import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Phase 6: Course recommendations.
 *
 * Calls the `recommend-courses` edge function which scores published
 * courses by category affinity (wishlist + cart + enrolled), recent
 * popularity, level match (when an anchor is provided), rating, and
 * free-course boost. Excludes already-enrolled / in-cart courses.
 */

export type RecommendationContext =
  | "dashboard"
  | "course_detail"
  | "cart"
  | "post_purchase";

export interface RecommendedCourse {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  category: string | null;
  level: string | null;
  price: number | null;
  is_free: boolean | null;
  thumbnail_url: string | null;
  rating: number | null;
}

export interface RecommendationItem {
  course: RecommendedCourse;
  score: number;
  reason: string;
  reasons: string[];
}

interface RecommendResult {
  context: RecommendationContext;
  items: RecommendationItem[];
}

interface Args {
  context?: RecommendationContext;
  courseId?: string;
  limit?: number;
  enabled?: boolean;
}

export function useRecommendations({
  context = "dashboard",
  courseId,
  limit = 6,
  enabled = true,
}: Args = {}) {
  const { user } = useAuth();

  return useQuery<RecommendResult>({
    queryKey: ["recommendations", context, courseId ?? "", limit, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("recommend-courses", {
        body: { context, courseId, limit },
      });
      if (error) throw error;
      return data as RecommendResult;
    },
    enabled: Boolean(user) && enabled,
    staleTime: 60_000,
  });
}
